import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Building2, Pencil, Plus } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { SuppliersManager } from '@/components/organisms/pricebook/SuppliersManager';
import { useSuppliers } from '@/hooks/pricebook/useSuppliers';
import type { Supplier } from '@/types/pricebook';
import { usePurchasingEnabled } from '@/features/purchasing/visibility';
import { useFeatureFlagsLoaded } from '@/stores/tenant.store';

export const Route = createFileRoute('/_dashboard/dashboard/settings/suppliers')({
  component: SuppliersSettingsPage,
});

/**
 * The companies we buy from, as a first-class settings surface.
 *
 * They are tenant-wide and shared by every material and equipment item, so
 * they belong here rather than only inside whichever item happened to need one
 * first — and this is where you come to add several before touching a catalog
 * item at all.
 */
function SuppliersSettingsPage() {
  // Where purchasing is on (PLAT-FF-1) suppliers live in Purchasing, with
  // their orders and bills; this address takes you there. Everywhere else
  // this is the supplier list, as it always was.
  const purchasingEnabled = usePurchasingEnabled();
  const flagsLoaded = useFeatureFlagsLoaded();
  if (purchasingEnabled) return <Navigate to="/dashboard/purchasing/suppliers" replace />;
  if (!flagsLoaded) return null;
  return <SuppliersList />;
}

function SuppliersList() {
  // Inactive suppliers are shown here (unlike the item picker): this is the
  // page where you would go to reactivate one.
  const { data: suppliers = [], isLoading } = useSuppliers(true);
  const [editorOpen, setEditorOpen] = useState(false);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Suppliers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The companies you buy materials and equipment from. Each item picks its own suppliers
            and one of them as primary, so a supplier is never primary everywhere — only for the
            items you say.
          </p>
        </div>
        <Button type="button" onClick={() => setEditorOpen(true)}>
          <Plus size={14} className="mr-1" /> New supplier
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : suppliers.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center">
          <Building2 size={20} className="mx-auto text-muted-foreground" />
          <p className="mt-2 text-sm font-medium">No suppliers yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Add the companies you buy from, then attach them to materials and equipment with the
            cost each one charges.
          </p>
          <Button type="button" className="mt-4" onClick={() => setEditorOpen(true)}>
            <Plus size={14} className="mr-1" /> New supplier
          </Button>
        </div>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {suppliers.map((supplier) => (
            <SupplierCard key={supplier.id} supplier={supplier} />
          ))}
        </ul>
      )}

      <SuppliersManager open={editorOpen} onOpenChange={setEditorOpen} hideTrigger />
    </div>
  );
}

function SupplierCard({ supplier }: { supplier: Supplier }) {
  const [open, setOpen] = useState(false);
  const details = [
    supplier.accountNumber && `Acct ${supplier.accountNumber}`,
    supplier.phone,
    supplier.email,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="group rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <Building2 size={15} className="mt-0.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0">
            <p className="truncate font-medium">
              {supplier.name}
              {supplier.isActive === false && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">inactive</span>
              )}
            </p>
            {details && <p className="mt-0.5 truncate text-xs text-muted-foreground">{details}</p>}
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
          aria-label={`Edit ${supplier.name}`}
          onClick={() => setOpen(true)}
        >
          <Pencil size={13} />
        </Button>
      </div>
      <SuppliersManager open={open} onOpenChange={setOpen} hideTrigger />
    </li>
  );
}
